import tenant from '../../config/tenant';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import attendanceService from '../../services/AttendanceService';
import { getAuthKeyFromLocalStorage } from './employeeSlice';

// ============================================================
// Search Employee
// ============================================================

export const searchEmployee = createAsyncThunk(
  'adjustment/searchEmployee',
  async ({ searchValue, year, month }, { rejectWithValue }) => {
    try {
      // Get auth token for API call
      const token = getAuthKeyFromLocalStorage();

      let response = null;
      let employeeData = null;

      // Search by Service Number
      try {
        response = await attendanceService.GetFilteredAttendanceDetails({
          year,
          month,
          sno: searchValue,
        });

        if (response?.ResultSet?.length > 0) {
          employeeData = response.ResultSet;
        }
      } catch (error) {
        // Continue to next search method
      }

      if (!employeeData?.length) {
        try {
          response = await attendanceService.GetFilteredAttendanceDetails({
            year,
            month,
          });

          if (response?.ResultSet?.length > 0) {
            const found = response.ResultSet.find((item) => {
              const itemId =
                item.EmployeeID ||    
                item.ServiceNo ||
                item.employeeId ||
                item.sno ||
                item.empId ||
                item.EmployeeId ||
                item.Sno ||
                '';

              return String(itemId) === String(searchValue);
            });

            if (found) {
              employeeData = [found];
            }
          }
        } catch (error) {
          // Search failed
        }
      }

      // If still no employee found, try the Employee API directly
      if (!employeeData?.length) {
        try {
          // Fetch all employees and search by service number
          const empResponse = await fetch(`${tenant.api.baseUrl}/EMP/GetEmployees`, {
            headers: {
              'Content-Type': 'application/json',
              'auth-key': token,
            },
          });

          const empData = await empResponse.json();
          const employees = empData?.ResultSet || [];

          const found = employees.find((item) => {
            const serviceNo = item.HED_SERVICE_NO || '';
            const employeeId = String(item.HED_EMPLOYEE_ID || '');
            return serviceNo === searchValue || employeeId === searchValue;
          });

          if (found) {
            employeeData = [{
              EmployeeID: String(found.HED_EMPLOYEE_ID || ''),
              Name: found.HED_REPORT_NAME || `${found.HED_FIRST_NAME || ''} ${found.HED_LAST_NAME || ''}`.trim(),
              Department: found.HDD_DEPARTMENT_NAME || '',
              Location: found.HED_LOCATION_ID || '',
              Designation: found.HED_DESIGNATION_NAME || '',
            }];
          }
        } catch (error) {
          // Employee API search failed
        }
      }

      if (!employeeData?.length) {
        return rejectWithValue(
          `Employee with ID "${searchValue}" not found. Please check the ID.`
        );
      }

      const data = employeeData[0];

      // Set today's date when employee is first selected
      const today = new Date();
      const todayDate = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, '0'),
        String(today.getDate()).padStart(2, '0'),
      ].join('-');

      return {
        employee: {
          serviceNo:
            data.ServiceNo ||
            data.employeeId ||
            data.sno ||
            data.empId ||
            searchValue,

          employeeId:
            data.EmployeeID ||
            data.EmployeeId ||
            data.employeeID ||
            data.employeeId ||
            '',

          name:
            data.Name ||
            data.employeeName ||
            data.name ||
            data.fullName ||
            '',

          department:
            data.Department ||
            data.department ||
            data.deptName ||
            '',

          location:
            data.Location ||
            data.location ||
            '',

          designation:
            data.Designation ||
            data.designation ||
            data.designationName ||
            '',
        },

        // Automatically select today's date
        attendanceDate: todayDate,

        // Initial attendance data
        currentAttendance: {
          inTime:
            data.InTime ||
            data.inTime ||
            '—',

          outTime:
            data.OutTime ||
            data.outTime ||
            '—',

          status:
            data.Status ||
            data.status ||
            'No Record',

          source:
            data.Source ||
            data.source ||
            '—',

          attDate:
            data.AttDate ||
            data.attDate ||
            '',
        },
      };
    } catch (error) {
      return rejectWithValue(
        error.message ||
        'Failed to fetch employee details. Please try again.'
      );
    }
  }
);


// ============================================================
// Fetch Attendance For Selected Date
// ============================================================

export const fetchAttendanceForDate = createAsyncThunk(
  'adjustment/fetchAttendanceForDate',
  async ({ sno, attendanceDate }, { rejectWithValue }) => {
    if (!sno || !attendanceDate) {
      return rejectWithValue(
        'Employee ID and date are required'
      );
    }

    try {
      const dateObj = new Date(`${attendanceDate}T00:00:00`);

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');

      const response =
        await attendanceService.GetFilteredAttendanceDetails({
          year,
          month,
          sno,
        });

      const records = response?.ResultSet || [];

      // Find attendance record for the selected date
      const attendanceData = records.find((item) => {
        if (!item.AttDate) {
          return false;
        }

        const normalizedApiDate =
          String(item.AttDate).substring(0, 10);

        return normalizedApiDate === attendanceDate;
      });

      if (attendanceData) {
        return {
          inTime:
            attendanceData.InTime ||
            attendanceData.inTime ||
            '—',

          outTime:
            attendanceData.OutTime ||
            attendanceData.outTime ||
            '—',

          status:
            attendanceData.Status ||
            attendanceData.status ||
            'No Record',

          source:
            attendanceData.Source ||
            attendanceData.source ||
            '—',

          attDate:
            attendanceData.AttDate ||
            attendanceData.attDate ||
            attendanceDate,
        };
      }

      // No attendance record for selected date
      return {
        inTime: '—',
        outTime: '—',
        status: 'No Record',
        source: '—',
        attDate: attendanceDate,
      };
    } catch (error) {
      return rejectWithValue(
        error.message ||
        'Failed to fetch attendance data'
      );
    }
  }
);


// ============================================================
// Submit Attendance Adjustment
// ============================================================

export const submitAdjustment = createAsyncThunk(
  'adjustment/submitAdjustment',
  async (adjustmentData, { rejectWithValue }) => {
    try {
      await attendanceService.PostAttendAdj(adjustmentData);

      return {
        success: true,
      };
    } catch (error) {
      let errorMessage =
        'Failed to submit attendance adjustment.';

      if (error.message?.includes('500')) {
        errorMessage =
          'Server error (500). Please try again or contact support.';
      } else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          if (
            error.response.data.includes(
              'DirectoryNotFoundException'
            )
          ) {
            errorMessage =
              'Backend configuration error. Please contact support.';
          } else {
            errorMessage = error.response.data;
          }
        } else if (error.response.data?.Message) {
          errorMessage = error.response.data.Message;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);


// ============================================================
// Initial State
// ============================================================

const initialState = {
  employee: {
    serviceNo: '',
    employeeId: '',
    name: '',
    department: '',
    location: '',
    designation: '',
  },

  currentAttendance: null,

  isSearching: false,
  searchError: '',

  attendanceDate: '',

  adjustmentType: 'Incorrect IN Time',

  adjustedInTime: '',
  adjustedOutTime: '',

  reason: '',

  file: null,

  submitting: false,
  submitError: '',
  submitSuccess: false,

  attendanceCache: {},
};


// ============================================================
// Slice
// ============================================================

const adjustmentSlice = createSlice({
  name: 'adjustment',

  initialState,

  reducers: {
    setAttendanceDate: (state, action) => {
      state.attendanceDate = action.payload;
    },

    setAdjustmentType: (state, action) => {
      state.adjustmentType = action.payload;
      state.adjustedInTime = '';
      state.adjustedOutTime = '';
    },

    setAdjustedInTime: (state, action) => {
      state.adjustedInTime = action.payload;
    },

    setAdjustedOutTime: (state, action) => {
      state.adjustedOutTime = action.payload;
    },

    setReason: (state, action) => {
      state.reason = action.payload;
    },

    setFile: (state, action) => {
      state.file = action.payload;
    },

    setSubmitError: (state, action) => {
      state.submitError = action.payload;
    },

    clearSearchError: (state) => {
      state.searchError = '';
    },

    clearSubmitError: (state) => {
      state.submitError = '';
    },

    resetForm: (state) => {
      state.employee = {
        serviceNo: '',
        name: '',
        department: '',
        location: '',
        designation: '',
      };

      state.currentAttendance = null;

      state.attendanceDate = '';

      state.adjustmentType = 'Incorrect IN Time';

      state.adjustedInTime = '';
      state.adjustedOutTime = '';

      state.reason = '';

      state.file = null;

      state.submitError = '';
      state.submitSuccess = false;

      state.searchError = '';
    },

    clearAdjustmentState: (state) => {
      Object.assign(state, initialState);
    },
  },


  // ==========================================================
  // Extra Reducers
  // ==========================================================

  extraReducers: (builder) => {
    builder

      // --------------------------------------------------------
      // Search Employee
      // --------------------------------------------------------

      .addCase(searchEmployee.pending, (state) => {
        state.isSearching = true;
        state.searchError = '';
        state.currentAttendance = null;
      })

      .addCase(searchEmployee.fulfilled, (state, action) => {
        state.isSearching = false;

        state.employee = action.payload.employee;

        // Automatically set today's date
        state.attendanceDate =
          action.payload.attendanceDate;

        state.currentAttendance =
          action.payload.currentAttendance;

        state.searchError = '';
      })

      .addCase(searchEmployee.rejected, (state, action) => {
        state.isSearching = false;

        state.searchError =
          action.payload ||
          'Failed to fetch employee details';

        state.employee = {
          serviceNo: '',
          name: '',
          department: '',
        };

        state.currentAttendance = null;
      })


      // --------------------------------------------------------
      // Fetch Attendance For Selected Date
      // --------------------------------------------------------

      .addCase(fetchAttendanceForDate.pending, (state) => {
        state.isSearching = true;
      })

      .addCase(
        fetchAttendanceForDate.fulfilled,
        (state, action) => {
          state.isSearching = false;

          if (action.payload) {
            state.currentAttendance =
              action.payload;

            const cacheKey =
              `${state.employee.serviceNo}_${state.attendanceDate}`;

            state.attendanceCache[cacheKey] =
              action.payload;
          }
        }
      )

      .addCase(
        fetchAttendanceForDate.rejected,
        (state) => {
          state.isSearching = false;
          state.currentAttendance = null;
        }
      )


      // --------------------------------------------------------
      // Submit Adjustment
      // --------------------------------------------------------

      .addCase(
        submitAdjustment.pending,
        (state) => {
          state.submitting = true;
          state.submitError = '';
          state.submitSuccess = false;
        }
      )

      .addCase(
        submitAdjustment.fulfilled,
        (state) => {
          state.submitting = false;
          state.submitSuccess = true;

          state.employee = {
            serviceNo: '',
            name: '',
            department: '',
          };

          state.currentAttendance = null;

          state.attendanceDate = '';

          state.adjustmentType =
            'Incorrect IN Time';

          state.adjustedInTime = '';
          state.adjustedOutTime = '';

          state.reason = '';

          state.file = null;
        }
      )

      .addCase(
        submitAdjustment.rejected,
        (state, action) => {
          state.submitting = false;

          state.submitError =
            action.payload ||
            'Failed to submit adjustment';
        }
      );
  },
});


// ============================================================
// Actions
// ============================================================

export const {
  setAttendanceDate,
  setAdjustmentType,
  setAdjustedInTime,
  setAdjustedOutTime,
  setReason,
  setFile,
  setSubmitError,
  clearSearchError,
  clearSubmitError,
  resetForm,
  clearAdjustmentState,
} = adjustmentSlice.actions;


// ============================================================
// Selectors
// ============================================================

export const selectAdjustmentState =
  (state) => state.adjustment;

export const selectEmployee =
  (state) => state.adjustment.employee;

export const selectEmployeeId = (state) => state.adjustment.employee.employeeId;

export const selectEmployeeDepartment = (state) => state.adjustment.employee.department;
export const selectEmployeeLocation = (state) => state.adjustment.employee.location;
export const selectEmployeeDesignation = (state) => state.adjustment.employee.designation;

export const selectCurrentAttendance =
  (state) => state.adjustment.currentAttendance;

export const selectIsSearching =
  (state) => state.adjustment.isSearching;

export const selectSearchError =
  (state) => state.adjustment.searchError;

export const selectAttendanceDate =
  (state) => state.adjustment.attendanceDate;

export const selectAdjustmentType =
  (state) => state.adjustment.adjustmentType;

export const selectAdjustedInTime =
  (state) => state.adjustment.adjustedInTime;

export const selectAdjustedOutTime =
  (state) => state.adjustment.adjustedOutTime;

export const selectReason =
  (state) => state.adjustment.reason;

export const selectFile =
  (state) => state.adjustment.file;

export const selectSubmitting =
  (state) => state.adjustment.submitting;

export const selectSubmitError =
  (state) => state.adjustment.submitError;

export const selectSubmitSuccess =
  (state) => state.adjustment.submitSuccess;

export default adjustmentSlice.reducer;

